import { type FormEvent, useEffect, useState } from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { ProjectColor, ProjectSummary } from "@/features/board/types";
import { cn } from "@/lib/utils";

type ProjectDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreate: (input: {
    name: string;
    description: string;
    color: ProjectColor;
  }) => Promise<ProjectSummary>;
};

const colors: Array<{ value: ProjectColor; label: string; className: string }> = [
  { value: "rose", label: "Rose", className: "bg-primary" },
  { value: "violet", label: "Violet", className: "bg-violet-500" },
  { value: "blue", label: "Blue", className: "bg-blue-500" },
  { value: "teal", label: "Teal", className: "bg-teal-500" },
  { value: "amber", label: "Amber", className: "bg-amber-500" },
];

export const ProjectDialog = ({ open, onClose, onCreate }: ProjectDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<ProjectColor>("rose");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setDescription("");
    setColor("rose");
    setError(null);
  }, [open]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onCreate({ name, description, color });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Project could not be created.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      description="Start with a ready-to-use four-column board."
      onClose={onClose}
      open={open}
      title="New project"
    >
      <form className="flex flex-col gap-4" onSubmit={submit}>
        {error ? <Alert className="border-0 bg-red-50 text-red-800 ring-1 ring-red-900/10">{error}</Alert> : null}
        <div className="flex flex-col gap-2">
          <Label htmlFor="project-name">Project name</Label>
          <Input
            autoFocus
            id="project-name"
            maxLength={80}
            name="name"
            onChange={(event) => setName(event.target.value)}
            placeholder="Website relaunch"
            required
            value={name}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="project-description">Description</Label>
          <Textarea
            className="min-h-24"
            id="project-description"
            maxLength={500}
            name="description"
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What is this team shipping?"
            value={description}
          />
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-base font-medium text-neutral-800 sm:text-sm">Project color</legend>
          <div className="flex gap-2">
            {colors.map((option) => (
              <button
                aria-label={option.label}
                aria-pressed={color === option.value}
                className={cn(
                  "relative size-8 rounded-full ring-2 ring-transparent ring-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary before:absolute before:top-1/2 before:left-1/2 before:size-[max(100%,3rem)] before:-translate-1/2 before:content-[''] pointer-fine:before:hidden",
                  option.className,
                  color === option.value && "ring-neutral-950"
                )}
                key={option.value}
                onClick={() => setColor(option.value)}
                type="button"
              />
            ))}
          </div>
        </fieldset>
        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={onClose} type="button" variant="ghost">
            Cancel
          </Button>
          <Button disabled={saving} type="submit">
            {saving ? "Creating" : "Create project"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
